import { inject, Service } from '@angular/core';
import { APP_ENVIRONMENT } from '@lexplus-environments';

/**
 * Gates the automatic sign-in that the login form performs from the gitignored
 * `dev-credentials.json`, on a localhost dev server only.
 *
 * The suppression lives in `localStorage`, which is scoped per origin, so it covers one dev
 * server — every tab and every reload of that worktree's app — rather than a single tab. An
 * explicit logout suppresses it, so a different account can be signed in by hand; visiting the
 * login route with `?autologin` lifts the suppression again.
 */
@Service()
export class LocalDevAutoLoginService {
  private static readonly SUPPRESSED_KEY = 'lexplus_dev_auto_login_suppressed';

  private readonly appEnvironment = inject(APP_ENVIRONMENT);

  /** Query parameter that re-enables the automatic sign-in after a logout suppressed it. */
  private readonly forceQueryParameter = 'autologin';

  /** One instance per page load, so a route re-entry cannot repeat the attempt. */
  private hasAttempted = false;

  public shouldSignIn(): boolean {
    if (this.appEnvironment.name !== 'localhost' || this.hasAttempted) {
      return false;
    }

    if (new URLSearchParams(window.location.search).has(this.forceQueryParameter)) {
      localStorage.removeItem(LocalDevAutoLoginService.SUPPRESSED_KEY);
      return true;
    }

    return !localStorage.getItem(LocalDevAutoLoginService.SUPPRESSED_KEY);
  }

  public markAttempted(): void {
    this.hasAttempted = true;
  }

  public suppress(): void {
    if (this.appEnvironment.name !== 'localhost') {
      return;
    }

    localStorage.setItem(LocalDevAutoLoginService.SUPPRESSED_KEY, 'true');
  }
}
